import React, { useMemo, forwardRef, Ref, useCallback, useEffect } from 'react';
import useRequest from '@gp/hooks/useRequest';
import { Card } from 'tea-component';
import Loading from '@gp/components/Loading';
import PencilEdit from '@gp/components/PencilEdit';
import Organizations from '@gp/pages/Pipeline/ProjectDetails/Stakeholder/components/StakeholderDetails/components/Organizations';
import { fetchStakeholders } from '@gp/services/stakeholder';
import '@gp/pages/Pipeline/ProjectDetails/Stakeholder/components/StakeholderDetails/index.scss';
import GroupSvg from '@gp/assets/svg/group.svg';
import classNames from 'classnames';
import {
  STAKEHOLDERS_SORT_KEYS,
  ORGANIZATIONS_SORT_KEYS,
  DEPARTMENTS_SORT_KEYS,
} from '@gp/pages/Pipeline/ProjectDetails/Stakeholder/consts';
import CardSlot from '@/pages/globalpedia/components/CardSlot';
import useAutoScroll2TargetModule from '../../../_hooks/useAutoScroll2TargetModule';

interface Props {
  pageContainer: React.RefObject<HTMLElement>;
  projectDetailData: any;
  isEditable: boolean;
  onEdit: (data: any) => void;
  onRefreshProjectDetails?: () => void;
  [keyName: string]: any;
}

interface ForwardProps {}

const FULL_STAKEHOLDERS_NAMES = [
  'Project Core Stakeholders',
  // 'Studio-Level Stakeholders',
];

const PROJECT_CORE_STAKEHOLDERS_FULL_ORGANIZATIONS_NAMES = ['iegg', 'studio'];

const FULL_ORGANIZATIONS_NAMES: Record<string, string[]> = {
  'Project Core Stakeholders': PROJECT_CORE_STAKEHOLDERS_FULL_ORGANIZATIONS_NAMES,
};

const getEmptyStakeholderOrganizations = (stakeholder?: string): any[] => {
  if (!stakeholder) return [];
  const organizationsNames = FULL_ORGANIZATIONS_NAMES[stakeholder] || [];
  const organizations = organizationsNames?.map((organization) => {
    return { organization, owner_list: [], update_time: '' };
  });
  return organizations || [];
};

const Stakeholder = forwardRef((props: Props, ref: Ref<ForwardProps>) => {
  const { pageContainer, projectDetailData, isEditable, onEdit, onRefreshProjectDetails } = props;

  const {
    data: stakeholdersRes,
    loading,
    run: stakeholderListRun,
  } = useRequest(fetchStakeholders, {
    manual: true,
  });
  const { /* edit: isEditable = false, */ stakeholder_list: stakeholdersData = [] } = stakeholdersRes?.data?.data || {};

  const getFullEmptyStakeholders = (data: any[]): any[] => {
    try {
      const result = [...data];
      let temp: string;
      let tempStakeholder: any;
      let tempStakeholderOrganizations: any[];
      let isIncludes: boolean;
      for (let i = 0; i < FULL_STAKEHOLDERS_NAMES?.length; i++) {
        temp = FULL_STAKEHOLDERS_NAMES[i];
        if (!temp) continue;
        isIncludes = result?.find((item) => item?.stakeholder === temp);
        if (!isIncludes) {
          tempStakeholderOrganizations = getEmptyStakeholderOrganizations(temp);
          tempStakeholder = { stakeholder: temp, organization_list: tempStakeholderOrganizations };
          result.push(tempStakeholder);
        }
      }
      for (const resultItem of result) {
        const resultItemOrganizations = resultItem?.organization_list;
        const resultItemOrganizationsStd = getEmptyStakeholderOrganizations(resultItem?.stakeholder);
        for (const resultItemOrganizationStd of resultItemOrganizationsStd) {
          const resultItemOrganizationMatched = resultItemOrganizations?.find(
            (item: any) => item?.organization === resultItemOrganizationStd?.organization,
          );
          if (!resultItemOrganizationMatched) {
            resultItemOrganizations.push(resultItemOrganizationStd);
          }
        }
      }
      return result;
    } catch (exception) {
      console.warn(exception);
      return FULL_STAKEHOLDERS_NAMES?.map((stakeholder) => {
        const organizations = getEmptyStakeholderOrganizations(stakeholder);
        return {
          stakeholder,
          organization_list: organizations,
        };
      });
    }
  };

  const getCommonSortedData = (data: any[], sortKeys: string[], sortKey: string): any[] => {
    try {
      return data.sort((a: any, b: any): number => {
        let aIndex = sortKeys.findIndex((item) => item === a?.[sortKey]);
        let bIndex = sortKeys.findIndex((item) => item === b?.[sortKey]);
        aIndex = aIndex < 0 ? data.length : aIndex;
        bIndex = bIndex < 0 ? data.length : bIndex;
        return aIndex - bIndex;
      });
    } catch (exception) {
      console.warn(exception);
      return [];
    }
  };

  const getSortedStakeholders = (data: any[]): any[] => {
    try {
      const dataTemp = data.map((stakeholder: any) => {
        const organizations = (stakeholder?.organization_list || []).map((organization: any) => {
          return {
            ...organization,
            owner_list: getCommonSortedData(organization?.owner_list, DEPARTMENTS_SORT_KEYS, 'project_owner'),
          };
        });
        return {
          ...stakeholder,
          organization_list: getCommonSortedData(organizations, ORGANIZATIONS_SORT_KEYS, 'organization'),
        };
      });
      return getCommonSortedData(dataTemp, STAKEHOLDERS_SORT_KEYS, 'stakeholder');
    } catch (exception) {
      console.warn(exception);
      return data;
    }
  };

  const stakeholders = useMemo(() => {
    if (!stakeholdersData || !Array.isArray(stakeholdersData))
      return getSortedStakeholders(getFullEmptyStakeholders([]));
    return getSortedStakeholders(getFullEmptyStakeholders(stakeholdersData));
  }, [stakeholdersData]);

  /* const handleEditClick = useCallback(() => {
    if (!isEditable) return;
    if (onEdit instanceof Function) {
      onEdit(stakeholders);
    }
  }, [isEditable, stakeholders, onEdit]); */

  // 默认请求页面数据
  useEffect(() => {
    projectDetailData?.project_id && stakeholderListRun({ project_id: projectDetailData?.project_id });
  }, [projectDetailData?.project_id]);

  useAutoScroll2TargetModule({
    pageContainer,
    loading,
  });

  if (loading) return <Loading loading style={{ height: 'calc(100vh - 400px)' }} />;

  return (
    <div className="dbg-detail-stakeholder-details">
      {stakeholders?.length > 0
        ? stakeholders?.map((stakeholder: any) => (
            <div
              key={stakeholder?.stakeholder}
              className={classNames('stakeholder-item', {
                'stakeholder-coming-soon': !stakeholder?.organization_list?.length,
              })}
            >
              <div className="dbg-detail-stakeholder-details-title">{stakeholder?.stakeholder}</div>
              <Organizations
                organizations={stakeholder?.organization_list}
                projectId={projectDetailData?.project_id}
                isEditable={isEditable}
                stakeholderListRun={stakeholderListRun}
                onRefreshProjectDetails={onRefreshProjectDetails}
              />
            </div>
          ))
        : null}
    </div>
  );
});

Stakeholder.defaultProps = {};

export default React.memo(Stakeholder);
// 旧结构
{
  /* <div className="dbg-detail-stakeholder-details">
      {stakeholders?.length > 0
        ? stakeholders?.map((stakeholder: any) => (
            <Card
              key={stakeholder?.stakeholder}
              className={classNames('stakeholder-item', {
                'stakeholder-coming-soon':
                  !stakeholder?.organization_list?.length,
              })}
            >
              <Card.Body
                title={
                  <>
                    <img className="titlebar-icon" src={GroupSvg} alt="" />
                    <span className="titlebar-text">
                      {stakeholder?.stakeholder}
                    </span>
                  </>
                }
                operation={
                  isEditable &&
                  stakeholder?.stakeholder === 'Project Core Stakeholders' ? (
                    <PencilEdit isEdit handleEditClick={handleEditClick} />
                  ) : null
                }
              >
                <Organizations organizations={stakeholder?.organization_list} />
              </Card.Body>
            </Card>
          ))
        : null}
    </div> */
}
